import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scale } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { useBodyClass } from '@/hooks/useBodyClass';
import { saveWeight } from '@/lib/weight';

/**
 * Окно ввода веса — одно на всё приложение (14.10.2026).
 *
 * Слава про вариант Б профиля: «с вводом веса непонятно — на главной нет,
 * только иногда появляется, это может запутать». Поэтому у веса одно
 * постоянное место — плитка в Профиле с кнопкой «+», а напоминание на главной
 * раз в неделю открывает ЭТО ЖЕ окно. Человек видит одинаковый ввод, где бы
 * его ни встретил.
 */
export function WeightEntrySheet({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { profile, setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useBodyClass('modal-open', open);

  useEffect(() => {
    if (open) { setValue(''); setError(''); }
  }, [open]);

  const w = Number(value.replace(',', '.'));
  const valid = w >= 30 && w <= 300;

  async function save() {
    if (!profile || !valid) return;
    setSaving(true);
    setError('');
    try {
      const updated = await saveWeight(profile, Math.round(w * 10) / 10);
      if (updated) setProfile(updated);
      haptic('success');
      onSaved?.();
      onClose();
    } catch (e) {
      console.error('[WeightEntrySheet]', e);
      haptic('error');
      setError('Не сохранилось. Проверь связь и попробуй ещё раз.');
    } finally {
      setSaving(false);
    }
  }

  const diff = profile?.weight_kg && valid ? Math.round((w - profile.weight_kg) * 10) / 10 : null;

  return (
    <AnimatePresence>
      {open && profile && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-bg-card rounded-t-3xl border-t border-line max-w-md mx-auto px-5 pt-4"
            style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="w-10 h-1 rounded-full bg-line mx-auto mb-4" />
            <div className="flex items-center gap-2 mb-1">
              <Scale size={16} className="text-sky-glow" />
              <span className="font-display font-semibold">Взвешивание</span>
            </div>
            <div className="text-xs text-ink-dim mb-4">
              Сейчас {profile.weight_kg} кг
              {profile.target_weight_kg ? ` · цель ${profile.target_weight_kg} кг` : ''}
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                className="input flex-1 text-lg"
                placeholder="Сколько на весах"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                autoFocus
              />
              <button
                onClick={save}
                disabled={saving || !valid}
                className="px-5 rounded-xl bg-sky-glow text-bg font-bold text-sm disabled:opacity-40"
              >
                {saving ? '…' : 'Сохранить'}
              </button>
            </div>
            <div className="text-[11px] text-ink-dim mt-2 min-h-[16px]">
              {error
                ? <span className="text-danger">{error}</span>
                : diff !== null && diff !== 0
                  ? `${diff > 0 ? '+' : '−'}${Math.abs(diff)} кг к прошлому замеру. Норма пересчитается от нового веса.`
                  : 'Лучше утром, натощак — раз в неделю достаточно, чтобы видеть тренд.'}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
